import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Box, Snackbar, Typography } from "@mui/material";
import { format } from "date-fns";
import { FormProvider, useForm } from "react-hook-form";
import { useLoaderData, useRevalidator } from "react-router";

import { useState } from "react";
import { SiliconForm } from "../../components/common/form/SiliconForm";
import { DynamicTable, TableColumn } from "../../components/common/table/Table";
import { postSilicon } from "../../services/siliconService";
import { PostSiliconPayload, SiliconEntryType } from "../../types/Silicon";
import { siliconValidationSchema } from "../../validation/yup/siliconSchema";

export default function SiliconPage() {
  const methods = useForm<PostSiliconPayload>({
    resolver: yupResolver(siliconValidationSchema),
  });

  const { entries: initialEntries } = useLoaderData() as {
    entries: SiliconEntryType[];
  };

  const revalidator = useRevalidator();
  const [entries, setEntries] = useState<SiliconEntryType[]>(initialEntries);
  const [operatorId, setOperatorId] = useState<number>(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (data: PostSiliconPayload) => {
    try {
      await postSilicon(data, operatorId);
      setShowSuccess(true);
      methods.reset();
      await revalidator.revalidate();
    } catch (err: any) {
      setErrorMessage(err.message || "Fehler beim Speichern");
      setShowError(true);
    }
  };

  const columns: TableColumn<SiliconEntryType>[] = [
    {
      key: "dateInProduction",
      label: "Produktionsdatum",
      render: (value) =>
        value ? format(new Date(value as string), "yyyy-MM-dd HH:mm:ss") : "-",
    },
    {
      key: "siliconBatch",
      label: "Batch Nummer",
    },
    {
      key: "expiryDate",
      label: "Ablaufdatum",
      render: (value) =>
        value ? format(new Date(value as string), "yyyy-MM-dd") : "-",
    },
    {
      key: "username",
      label: "Operator",
    },
  ];

  return (
    <Box p={4}>
      <Box display="flex" flexDirection="row" gap={4} alignItems="flex-start">
        {/* Form */}
        <Box flex={1}>
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handleSubmit)}>
              <SiliconForm operatorId={operatorId} />
            </form>
          </FormProvider>
        </Box>

        {/* Table */}
        <Box flex={3}>
          <Typography variant="h6" gutterBottom>
            Zuletzt erfasste Silicon-Einträge
          </Typography>
          <DynamicTable columns={columns} data={entries} />
        </Box>
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Silicon erfolgreich gespeichert!
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowError(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage || "Ein unbekannter Fehler ist aufgetreten."}
        </Alert>
      </Snackbar>
    </Box>
  );
}
